import React from 'react';
import mixpanel from 'mixpanel-browser';

import { Page, Card, InputGroup, Input, ExpandableCard, OpeningHoursInput } from '@nearst/ui';
import { updateChannel } from '@services/channels';
import { useCheckPermission } from '@services/auth/hooks';
import { useShop } from '@services/ShopProvider';
import PriceMarkupInput from '../PriceMarkupInput';

import styles from './DeliveryStoreSettings.module.scss';
import RoundingDropdown from '../RoundingDropdown';

const DeliveryStoreSettings = ({ channel, hasOpeningHours = false, fields = [] }) => {
	const { shop } = useShop();
	const authorized = useCheckPermission('org:channels:manage');

	const updateConfigField = async (target, value) => {
		if (target.readOnly || target.disabled) return;

		target.ariaBusy = true; // mark as loading

		await updateChannel(shop.id, channel.type, { [target.id]: value });

		target.ariaBusy = false;

		mixpanel.track('Delivery channel settings updated', {
			Channel: channel.type,
			Field: target.id,
			Value: value
		});
	};

	return (
		<>
			<Page.Section>
				<Card title="Store settings">
					<div className={styles.inputRow}>
						{fields.map(({ id, label, defaultValue, ...field }) => (
							<InputGroup label={label} key={id} htmlFor={id}>
								<Input
									id={id}
									{...field}
									disabled={!authorized}
									containerClassName={styles.settingsField}
									defaultValue={channel.config?.[id] || defaultValue?.(shop)}
									onBlur={({ target }) => updateConfigField(target, target.value)}
								/>
							</InputGroup>
						))}
					</div>
					<div className={styles.inputRow}>
						<PriceMarkupInput
							id="defaultDeliveryMarkup"
							label="Default price markup"
							value={channel.config?.defaultDeliveryMarkup}
							disabled={!authorized}
							onUpdate={updateConfigField}
						/>
						<PriceMarkupInput
							id="defaultTaxPercentage"
							label="Default tax rate"
							value={channel.config?.defaultTaxPercentage}
							disabled={!authorized}
							onUpdate={updateConfigField}
						/>
						<RoundingDropdown
							id="priceRounding"
							label="Price rounding"
							value={channel.config?.priceRounding}
							disabled={!authorized}
							onUpdate={updateConfigField}
							currency={shop.currency || shop.billingCurrency || 'GBP'}
						/>
					</div>
				</Card>
				{hasOpeningHours && authorized && (
					<ExpandableCard title="Opening hours">
						<OpeningHoursInput
							value={channel.config?.schedule}
							onChange={(value) => updateConfigField({ id: 'schedule' }, value)}
						/>
					</ExpandableCard>
				)}
			</Page.Section>
		</>
	);
};

export default DeliveryStoreSettings;
